import React, { useState, useEffect, useRef } from 'react';
import { G4sLayout } from '../../components/gestion4sat/G4sLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import {
  ChevronLeft, Loader2, Search, AlertTriangle, CheckCircle2,
  MapPin, Car, Building2, Cpu, RefreshCw, MapPinned
} from 'lucide-react';
import {
  createServicio, checkImei, scraperPatente, getEmpresas, buscarImeiPredictivo
} from '../../lib/gestion4satApi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const TIPOS = [
  { value: 'instalacion', label: 'Instalaci\u00f3n' },
  { value: 'mantencion', label: 'Mantenci\u00f3n' },
  { value: 'asistencia', label: 'Asistencia' },
  { value: 'revision', label: 'Revisi\u00f3n' },
  { value: 'retiro', label: 'Retiro' },
  { value: 'otro', label: 'Otro' },
];

export default function G4sServicioFormPage() {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [imeiChecking, setImeiChecking] = useState(false);
  const [patenteLoading, setPatenteLoading] = useState(false);
  const [empresaSearch, setEmpresaSearch] = useState('');
  const [empresaResults, setEmpresaResults] = useState([]);
  const [empresaSearching, setEmpresaSearching] = useState(false);
  const [imeiInfo, setImeiInfo] = useState(null);
  const [imeiSuggestions, setImeiSuggestions] = useState([]);
  const [showImeiSuggestions, setShowImeiSuggestions] = useState(false);
  const searchTimeout = useRef(null);
  const imeiTimeout = useRef(null);

  const [form, setForm] = useState({
    tipo: '', tipo_otro_texto: '',
    imei: '', patente: '',
    empresa_id: '', empresa_razon_social: '',
    vehiculo_marca: '', vehiculo_modelo: '', vehiculo_anio: '',
    vehiculo_tipo: '', vehiculo_color: '', kilometraje: '',
    destino_retiro: '',
    notas: '', fecha_servicio: new Date().toISOString().slice(0, 10),
    coordenadas: null,
  });

  // Auto-capture GPS on mount
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      pos => {
        setForm(prev => ({...prev, coordenadas: { lat: pos.coords.latitude, lng: pos.coords.longitude }}));
      },
      () => {},
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const captureGPS = () => {
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        set('coordenadas', { lat: pos.coords.latitude, lng: pos.coords.longitude });
        setGpsLoading(false);
      },
      () => setGpsLoading(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Check IMEI when user stops typing + predictive search
  const handleImeiChange = (val) => {
    set('imei', val);
    const clean = val.replace(/\D/g, '');

    // Predictive search (3+ chars)
    if (imeiTimeout.current) clearTimeout(imeiTimeout.current);
    if (clean.length >= 3 && clean.length < 15) {
      imeiTimeout.current = setTimeout(async () => {
        try {
          const res = await buscarImeiPredictivo(clean);
          setImeiSuggestions(res.resultados || []);
          setShowImeiSuggestions(true);
        } catch { setImeiSuggestions([]); }
      }, 300);
    } else {
      setImeiSuggestions([]);
      setShowImeiSuggestions(false);
    }

    // Full IMEI check (15 chars)
    if (clean.length >= 15) {
      setShowImeiSuggestions(false);
      setImeiChecking(true);
      checkImei(clean).then(data => {
        setImeiInfo(data);
        if (data.found) {
          if (data.empresa_id) {
            set('empresa_id', data.empresa_id);
            set('empresa_razon_social', data.empresa_razon_social);
          }
          if (data.patente) set('patente', data.patente);
        }
        setImeiChecking(false);
      }).catch(() => setImeiChecking(false));
    } else {
      setImeiInfo(null);
    }
  };

  const selectImeiSuggestion = (device) => {
    set('imei', device.imei);
    setShowImeiSuggestions(false);
    setImeiSuggestions([]);
    handleImeiChange(device.imei);
  };

  // Scrape patentechile.com
  const handlePatenteBlur = async () => {
    const pat = form.patente.trim();
    if (!pat || pat.length < 4) return;
    if (form.vehiculo_marca && form.vehiculo_modelo) return;

    setPatenteLoading(true);
    try {
      // Try backend first (uses cache + server-side scraper)
      const res = await scraperPatente(pat);
      if (res.datos) {
        const d = res.datos;
        setForm(prev => ({
          ...prev,
          vehiculo_marca: d.marca || prev.vehiculo_marca,
          vehiculo_modelo: d.modelo || prev.vehiculo_modelo,
          vehiculo_anio: d.anio || prev.vehiculo_anio,
          vehiculo_tipo: d.tipo || prev.vehiculo_tipo,
          vehiculo_color: d.color || prev.vehiculo_color,
        }));
        toast.success('Datos del veh\u00edculo cargados');
        setPatenteLoading(false);
        return;
      }
    } catch { /* continue to client-side */ }

    // Fallback: client-side fetch (user's IP bypasses Cloudflare)
    try {
      const resp = await fetch(`https://www.patentechile.com/patente/${pat.replace(/[^A-Za-z0-9]/g, '')}`, {
        mode: 'no-cors',
      });
      // no-cors won't give us the body, but signals network is reachable
    } catch { /* expected */ }

    // Open manual lookup helper
    toast.info('Scraper no disponible. Ingresa los datos del veh\u00edculo manualmente o consulta patentechile.com', {
      action: {
        label: 'Abrir patentechile.com',
        onClick: () => window.open(`https://www.patentechile.com/patente/${pat}`, '_blank'),
      },
      duration: 8000,
    });
    setPatenteLoading(false);
  };

  // Empresa search
  const handleEmpresaSearch = (val) => {
    setEmpresaSearch(val);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (val.length < 2) { setEmpresaResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setEmpresaSearching(true);
      try {
        const d = await getEmpresas({ q: val, limit: 8 });
        setEmpresaResults(d.empresas);
      } catch { /* ignore */ }
      setEmpresaSearching(false);
    }, 300);
  };

  const selectEmpresa = (emp) => {
    set('empresa_id', emp.id);
    set('empresa_razon_social', emp.razon_social);
    setEmpresaSearch('');
    setEmpresaResults([]);
  };

  const handleSubmit = async () => {
    if (!form.tipo) { toast.error('Selecciona un tipo de servicio'); return; }
    if (!form.imei && !form.patente) { toast.error('Ingresa IMEI o patente'); return; }

    setSaving(true);
    try {
      const result = await createServicio(form);
      toast.success('Servicio registrado');
      navigate(`/gestion4sat/servicios/${result.id}`);
    } catch (e) { toast.error(e.message); }
    setSaving(false);
  };

  const isReasignacion = imeiInfo?.found && imeiInfo?.empresa_id && form.empresa_id && imeiInfo.empresa_id !== form.empresa_id;

  return (
    <G4sLayout>
      <div className="space-y-6 max-w-2xl" data-testid="g4s-servicio-form">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/gestion4sat/servicios')}>
            <ChevronLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold tracking-tight">Nuevo Servicio</h1>
        </div>

        {/* Tipo de Actividad */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Tipo de Actividad</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {TIPOS.map(t => (
                <button
                  key={t.value}
                  onClick={() => set('tipo', t.value)}
                  className={`p-3 rounded-lg border text-xs font-medium text-center transition-all ${
                    form.tipo === t.value
                      ? 'border-emerald-500 bg-emerald-500/10 text-emerald-600'
                      : 'border-border hover:border-muted-foreground/30'
                  }`}
                  data-testid={`tipo-${t.value}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            {form.tipo === 'otro' && (
              <Input className="mt-3" placeholder="Descripci\u00f3n de la actividad..." value={form.tipo_otro_texto} onChange={e => set('tipo_otro_texto', e.target.value)} data-testid="tipo-otro-texto" />
            )}
            {form.tipo === 'retiro' && (
              <div className="mt-3">
                <Label>Destino del retiro</Label>
                <Select value={form.destino_retiro} onValueChange={v => set('destino_retiro', v)}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="stock">Stock / Bodega</SelectItem>
                    <SelectItem value="cliente">Otro Cliente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* IMEI */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Cpu className="h-4 w-4 text-blue-500" /> IMEI del Dispositivo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Input
                placeholder="Escanea o digita IMEI (3+ caracteres para buscar)..."
                value={form.imei}
                onChange={e => handleImeiChange(e.target.value)}
                onFocus={() => imeiSuggestions.length > 0 && setShowImeiSuggestions(true)}
                onBlur={() => setTimeout(() => setShowImeiSuggestions(false), 200)}
                className="font-mono"
                data-testid="input-imei"
              />
              {imeiChecking && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />}
              {/* Predictive suggestions dropdown */}
              {showImeiSuggestions && imeiSuggestions.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {imeiSuggestions.map(d => (
                    <button
                      key={d.id}
                      type="button"
                      className="w-full text-left px-3 py-2 hover:bg-muted/50 text-xs flex items-center justify-between border-b last:border-0"
                      onMouseDown={() => selectImeiSuggestion(d)}
                      data-testid={`imei-suggestion-${d.imei}`}
                    >
                      <span className="font-mono font-medium">{d.imei}</span>
                      <span className="text-muted-foreground flex items-center gap-2">
                        {d.patente_formateada && <span className="font-mono">{d.patente_formateada}</span>}
                        {d.empresa_razon_social && <span className="truncate max-w-[120px]">{d.empresa_razon_social}</span>}
                        <Badge variant="secondary" className="text-[9px]">{d.estado}</Badge>
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {imeiInfo && (
              <div className={`rounded-lg p-3 text-sm ${imeiInfo.found ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-amber-500/10 border border-amber-500/20'}`}>
                {imeiInfo.found ? (
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 font-medium">
                      <CheckCircle2 className="h-4 w-4 text-blue-500" />
                      Dispositivo encontrado
                    </div>
                    {imeiInfo.patente && <div className="text-xs">Patente: <span className="font-mono font-bold">{imeiInfo.patente}</span></div>}
                    {imeiInfo.empresa_razon_social && <div className="text-xs">Cliente actual: {imeiInfo.empresa_razon_social}</div>}
                    {imeiInfo.gps_marca && <div className="text-xs">GPS: {imeiInfo.gps_marca} {imeiInfo.gps_modelo}</div>}
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <span>IMEI no registrado en el sistema</span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Empresa */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-emerald-500" /> Empresa / Cliente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {form.empresa_id ? (
              <div className="flex items-center justify-between p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                <div>
                  <div className="font-medium text-sm">{form.empresa_razon_social}</div>
                  <div className="text-xs text-muted-foreground">ID: {form.empresa_id.slice(0, 8)}...</div>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { set('empresa_id', ''); set('empresa_razon_social', ''); }}>Cambiar</Button>
              </div>
            ) : (
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por RUT o raz\u00f3n social..."
                  value={empresaSearch}
                  onChange={e => handleEmpresaSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-empresa"
                />
                {empresaSearching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin" />}
                {empresaResults.length > 0 && (
                  <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-card border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                    {empresaResults.map(emp => (
                      <button
                        key={emp.id}
                        onClick={() => selectEmpresa(emp)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-muted transition-colors border-b last:border-0"
                        data-testid={`empresa-option-${emp.id}`}
                      >
                        <div className="font-medium">{emp.razon_social}</div>
                        <div className="text-xs text-muted-foreground font-mono">{emp.rut_formateado}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {isReasignacion && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-orange-500/10 border border-orange-500/20 text-sm">
                <AlertTriangle className="h-4 w-4 text-orange-500 shrink-0" />
                <div>
                  <div className="font-medium text-orange-700 dark:text-orange-400">Reasignaci&oacute;n detectada</div>
                  <div className="text-xs">Este IMEI pertenece a <strong>{imeiInfo.empresa_razon_social}</strong> y se mover&aacute; a <strong>{form.empresa_razon_social}</strong></div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Patente + Veh&iacute;culo */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Car className="h-4 w-4 text-indigo-500" /> Veh&iacute;culo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Label>Patente</Label>
              <Input
                placeholder="Ej: ABCD12"
                value={form.patente}
                onChange={e => set('patente', e.target.value.toUpperCase())}
                onBlur={handlePatenteBlur}
                className="font-mono uppercase"
                data-testid="input-patente"
              />
              {patenteLoading && <Loader2 className="absolute right-3 top-8 h-4 w-4 animate-spin text-muted-foreground" />}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Marca</Label>
                <Input value={form.vehiculo_marca} onChange={e => set('vehiculo_marca', e.target.value)} placeholder="Ej: MITSUBISHI" data-testid="input-marca" />
              </div>
              <div>
                <Label>Modelo</Label>
                <Input value={form.vehiculo_modelo} onChange={e => set('vehiculo_modelo', e.target.value)} placeholder="Ej: L200" data-testid="input-modelo" />
              </div>
              <div>
                <Label>A&ntilde;o</Label>
                <Input value={form.vehiculo_anio} onChange={e => set('vehiculo_anio', e.target.value)} placeholder="2024" data-testid="input-anio" />
              </div>
              <div>
                <Label>Tipo</Label>
                <Input value={form.vehiculo_tipo} onChange={e => set('vehiculo_tipo', e.target.value)} placeholder="CAMIONETA" />
              </div>
            </div>
            <div>
              <Label>Kilometraje (km)</Label>
              <Input type="number" value={form.kilometraje} onChange={e => set('kilometraje', e.target.value)} placeholder="Ej: 45320" data-testid="input-km" />
            </div>
          </CardContent>
        </Card>

        {/* Notas + Fecha + GPS */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Detalles del Servicio</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Fecha del servicio</Label>
              <Input type="date" value={form.fecha_servicio} onChange={e => set('fecha_servicio', e.target.value)} data-testid="input-fecha" />
            </div>
            <div>
              <Label>Notas / Observaciones</Label>
              <Textarea value={form.notas} onChange={e => set('notas', e.target.value)} placeholder="Detalles del trabajo realizado..." rows={3} data-testid="input-notas" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <MapPin className={`h-4 w-4 ${form.coordenadas ? 'text-green-500' : 'text-muted-foreground'}`} />
                  <span className="text-xs font-medium">Ubicaci&oacute;n GPS</span>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={captureGPS} disabled={gpsLoading} data-testid="btn-gps">
                    {gpsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-3 w-3 mr-1" /> Auto</>}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setShowMapPicker(true)} data-testid="btn-map-picker">
                    <MapPinned className="h-3 w-3 mr-1" /> Elegir en mapa
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Latitud</Label>
                  <Input
                    type="number" step="any"
                    value={form.coordenadas?.lat || ''}
                    onChange={e => set('coordenadas', { lat: parseFloat(e.target.value) || 0, lng: form.coordenadas?.lng || 0 })}
                    placeholder="-33.4489"
                    className="h-8 text-xs font-mono"
                    data-testid="input-lat"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Longitud</Label>
                  <Input
                    type="number" step="any"
                    value={form.coordenadas?.lng || ''}
                    onChange={e => set('coordenadas', { lat: form.coordenadas?.lat || 0, lng: parseFloat(e.target.value) || 0 })}
                    placeholder="-70.6693"
                    className="h-8 text-xs font-mono"
                    data-testid="input-lng"
                  />
                </div>
              </div>
              {form.coordenadas?.lat && form.coordenadas?.lng && (
                <a
                  href={`https://www.google.com/maps?q=${form.coordenadas.lat},${form.coordenadas.lng}`}
                  target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-500 hover:underline flex items-center gap-1"
                >
                  <MapPin className="h-3 w-3" /> Ver en Google Maps
                </a>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex gap-3">
          <Button variant="outline" onClick={() => navigate('/gestion4sat/servicios')} className="flex-1">Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="btn-guardar-servicio">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Registrar Servicio
          </Button>
        </div>
      </div>

      {/* Map Picker Dialog */}
      <Dialog open={showMapPicker} onOpenChange={setShowMapPicker}>
        <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="px-4 pt-4 pb-2">
            <DialogTitle>Elegir ubicaci&oacute;n en el mapa</DialogTitle>
            <DialogDescription>Haz click en el mapa para colocar el pin</DialogDescription>
          </DialogHeader>
          <MapPickerInner
            initialLat={form.coordenadas?.lat || -33.4489}
            initialLng={form.coordenadas?.lng || -70.6693}
            onConfirm={(lat, lng) => {
              set('coordenadas', { lat, lng });
              setShowMapPicker(false);
            }}
            onCancel={() => setShowMapPicker(false)}
          />
        </DialogContent>
      </Dialog>
    </G4sLayout>
  );
}

function MapPickerInner({ initialLat, initialLng, onConfirm, onCancel }) {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [lat, setLat] = useState(initialLat);
  const [lng, setLng] = useState(initialLng);

  useEffect(() => {
    if (mapRef.current) return; // already initialized
    const L = window.L;
    if (!L) {
      // Load Leaflet CSS/JS dynamically
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = () => initMap();
      document.head.appendChild(script);
    } else {
      initMap();
    }

    function initMap() {
      const L = window.L;
      const map = L.map('map-picker-container').setView([initialLat, initialLng], 13);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap',
      }).addTo(map);

      const marker = L.marker([initialLat, initialLng], { draggable: true }).addTo(map);
      markerRef.current = marker;
      mapRef.current = map;

      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        setLat(pos.lat);
        setLng(pos.lng);
      });

      map.on('click', (e) => {
        marker.setLatLng(e.latlng);
        setLat(e.latlng.lat);
        setLng(e.latlng.lng);
      });

      setTimeout(() => map.invalidateSize(), 200);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  return (
    <div className="flex flex-col">
      <div id="map-picker-container" style={{ height: '400px', width: '100%' }} />
      <div className="p-4 flex items-center justify-between border-t">
        <span className="text-xs font-mono text-muted-foreground">
          {lat.toFixed(6)}, {lng.toFixed(6)}
        </span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onCancel}>Cancelar</Button>
          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => onConfirm(lat, lng)} data-testid="btn-confirm-map-location">
            Confirmar ubicaci&oacute;n
          </Button>
        </div>
      </div>
    </div>
  );
}
